import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, userIsAdmin, userIsAccountant } from '../context/AuthContext';
import { VA_BLACK, AMBER, BASE_CSS, BORDER_COLOR, fieldStyle } from '../styles/tokens';

export default function LoginPage() {
  const { login, loginWithSSO, ssoError } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');

  function handleSuccess(user) {
    navigate(
      userIsAdmin(user)      ? '/admin' :
      userIsAccountant(user) ? '/contabilidad' :
      '/dashboard'
    );
  }

  function handleSSO() {
    setError('');
    setLoading(true);
    // Redirects to Microsoft — page will reload when user returns
    loginWithSSO();
  }

  async function handlePassword(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      handleSuccess(await login(email, pass));
    } catch (err) {
      setError(err.response?.data?.error || 'Correo o contraseña incorrectos.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#f8fafc',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Inter, ui-sans-serif, sans-serif',
      padding: '20px 16px',
    }}>
      <style>{BASE_CSS}</style>

      <div style={{ width: '100%', maxWidth: 400, animation: 'fadeUp 0.3s ease' }}>
        {/* Header */}
        <div style={{
          background: VA_BLACK, color: '#fff',
          borderRadius: '16px 16px 0 0',
          padding: '24px 28px',
          textAlign: 'center',
        }}>
          <img
            src="/newlogo.jpeg"
            alt="Villavicencio & Associates"
            style={{ height: 40, width: 'auto' }}
          />
        </div>

        {/* Card body */}
        <div style={{
          background: '#fff',
          border: `1px solid ${BORDER_COLOR}`,
          borderTop: 'none',
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: '32px 32px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}>
          <p style={{
            fontSize: 14, color: '#475569', textAlign: 'center',
            margin: '0 0 24px', lineHeight: 1.5,
          }}>
            Usa tu cuenta de Microsoft para acceder al portal.
          </p>

          {(error || ssoError) && (
            <div style={{
              background: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca', borderRadius: 6,
              padding: '8px 12px', fontSize: 13, marginBottom: 16,
              width: '100%', textAlign: 'center',
            }}>
              {error || ssoError}
            </div>
          )}

          {/* Microsoft SSO button */}
          <button
            onClick={handleSSO}
            disabled={loading}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: loading ? '#94a3b8' : '#2f2f2f',
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '12px 0', fontWeight: '600', fontSize: 15,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'Inter, sans-serif',
              transition: 'background 0.15s',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            {loading && !showPassword ? 'Conectando…' : 'Iniciar sesión con Microsoft'}
          </button>

          {/* Divider + password toggle */}
          <button
            onClick={() => setShowPassword(v => !v)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, color: '#94a3b8', marginTop: 20,
              fontFamily: 'Inter, sans-serif',
              textDecoration: 'underline', textUnderlineOffset: 2,
            }}
          >
            {showPassword ? 'Ocultar acceso con contraseña' : 'Acceso con contraseña'}
          </button>

          {/* Password form (collapsible) */}
          {showPassword && (
            <form onSubmit={handlePassword} style={{ width: '100%', marginTop: 16 }}>
              <div style={{
                borderTop: '1px solid #e2e8f0', paddingTop: 16, marginBottom: 16,
              }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  style={fieldStyle}
                  autoComplete="email"
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  Contraseña
                </label>
                <input
                  type="password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={fieldStyle}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: loading ? '#94a3b8' : AMBER,
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '12px 0', fontWeight: '600', fontSize: 15,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'background 0.15s',
                }}
              >
                {loading && showPassword ? 'Iniciando sesión…' : 'Iniciar sesión →'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
